import { Routes, Route, Navigate } from 'react-router-dom'
import Splash from './pages/Splash'
import AthleteWorkout from './pages/AthleteWorkout'
import CoachDashboard from './pages/CoachDashboard'
import CoachLogin from './pages/CoachLogin'
import SetPassword from './pages/SetPassword'
import CoachTemplates from './pages/CoachTemplates'
import ProgramBuilder from './pages/ProgramBuilder'
import GymView from './pages/GymView'
import AthleteReport from './pages/AthleteReport'
import { useParams } from 'react-router-dom'
import { useSession } from './lib/session'

/** Signed in, but not a coach and not linked to an athlete (or lookup failed). */
function NoAccess() {
  const { signOut } = useSession()
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 p-8 text-center">
      <p className="font-display text-2xl tracking-wide">No access yet</p>
      <p className="max-w-sm text-sm text-muted">
        This account isn’t linked to an athlete profile. If you should have access, ask your
        coach — or try again if your connection dropped.
      </p>
      <div className="flex gap-2">
        <button onClick={() => window.location.reload()} className="rounded-btn border border-border-strong px-4 py-2 text-sm hover:border-gold">
          Try again
        </button>
        <button
          onClick={async () => { await signOut(); window.location.href = '/login' }}
          className="rounded-btn px-4 py-2 text-sm text-muted hover:text-text"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}

/** Any signed-in user; bounce to login when signed out. */
function RequireAuth({ children }: { children: JSX.Element }) {
  const { session, loading, ready } = useSession()
  if (ready && loading) return null
  if (ready && !session) return <Navigate to="/login" replace />
  return children
}

/** Coach-only routes. Athletes are redirected to their own workout. */
function RequireCoach({ children }: { children: JSX.Element }) {
  const { session, loading, ready, role, athleteId, roleResolved } = useSession()
  if (ready && loading) return null
  if (ready && !session) return <Navigate to="/login" replace />
  if (ready && !roleResolved) return null
  if (role === 'athlete') return <Navigate to={athleteId ? `/athlete/${athleteId}` : '/login'} replace />
  if (role !== 'coach') return <NoAccess />
  return children
}

/** Athlete pages: coaches see anyone; an athlete only ever sees their own. */
function RequireAthletePage({ children }: { children: JSX.Element }) {
  const { id } = useParams()
  const { session, loading, ready, role, athleteId, roleResolved } = useSession()
  if (ready && loading) return null
  if (ready && !session) return <Navigate to="/login" replace />
  if (ready && !roleResolved) return null
  if (role === 'athlete') {
    return athleteId && id !== athleteId ? <Navigate to={`/athlete/${athleteId}`} replace /> : children
  }
  if (role === 'none') return <NoAccess />
  return children
}

/** Home: coaches get the athlete-search splash, athletes get their workout. */
function Home() {
  const { role, athleteId, roleResolved, ready } = useSession()
  if (ready && !roleResolved) return null
  if (role === 'athlete') return <Navigate to={athleteId ? `/athlete/${athleteId}` : '/login'} replace />
  if (role === 'none') return <NoAccess />
  return <Splash />
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<CoachLogin />} />
      {/* Back-compat with the old coach login path */}
      <Route path="/coach/login" element={<Navigate to="/login" replace />} />
      {/* Invite / reset link lands here — public so the token can establish a session. */}
      <Route path="/set-password" element={<SetPassword />} />

      <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
      <Route path="/athlete/:id" element={<RequireAthletePage><AthleteWorkout /></RequireAthletePage>} />

      <Route path="/athlete/:id/report" element={<RequireCoach><AthleteReport /></RequireCoach>} />

      <Route path="/coach" element={<RequireCoach><CoachDashboard /></RequireCoach>} />
      <Route path="/coach/builder" element={<RequireCoach><ProgramBuilder /></RequireCoach>} />
      <Route path="/coach/templates" element={<RequireCoach><CoachTemplates /></RequireCoach>} />
      <Route path="/gym" element={<RequireCoach><GymView /></RequireCoach>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
